/**
 * AppHeader Component
 *
 * Global navigation header - always visible except on immersive pages.
 * Based on UX_DESIGN.md Navigation Structure (lines 592-624).
 *
 * Features:
 * - Logo (links to home)
 * - Main navigation links (Browse Datasets, My Quizzes, Map of Knowledge, Stats)
 * - User menu
 * - Mobile hamburger menu
 * - Optional progress bar
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserMenu } from "@/components/auth/UserMenu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, Library, TrendingUp, Database, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { isOnline, setupOnlineOfflineListeners } from "@/utils/networkStatus";

interface AppHeaderProps {
  /**
   * Show progress bar at top of header (for quiz play)
   */
  showProgress?: boolean;
  /**
   * Progress percentage (0-100)
   */
  progress?: number;
}

export function AppHeader({
  showProgress = false,
  progress = 0,
}: AppHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Online/offline state
  const [online, setOnline] = useState(() => isOnline());

  useEffect(() => {
    const cleanup = setupOnlineOfflineListeners(
      () => setOnline(true),
      () => setOnline(false)
    );
    return cleanup;
  }, []);

  // Navigation items
  const navItems = [
    {
      label: "Browse Datasets",
      icon: Database,
      path: "/geojson/browse",
    },
    {
      label: "My Quizzes",
      icon: Library,
      path: "/quiz/my-quizzes",
    },
    {
      label: "Progress",
      icon: TrendingUp,
      path: "/progress",
    },
  ];

  // Check if current path matches nav item
  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-header bg-card border-b">
      {/* Progress bar at very top */}
      {showProgress && (
        <div
          className="h-1 bg-primary transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      )}

      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        {/* Left: Logo + Brand + Offline Indicator */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img
              src="/logo.svg"
              alt="MapQuizzr Logo"
              className="w-8 h-8 drop-shadow-sm"
            />
            <span className="font-bold text-lg hidden sm:inline">MapQuizzr</span>
          </Link>

          {/* Offline Indicator */}
          {!online && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 flex items-center gap-1">
              <WifiOff className="w-3 h-3" />
              <span className="hidden sm:inline">Offline</span>
            </Badge>
          )}
        </div>

        {/* Center: Navigation Links (always visible on desktop) */}
        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Button
                key={item.path}
                variant={active ? "secondary" : "ghost"}
                size="sm"
                onClick={() => navigate(item.path)}
                className={cn(
                  "gap-2",
                  active && "bg-primary/10 text-primary hover:bg-primary/20"
                )}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden lg:inline">{item.label}</span>
              </Button>
            );
          })}
        </nav>

        {/* Right: Mobile Menu + User Menu */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Mobile hamburger menu */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={cn(
                        "gap-2 cursor-pointer",
                        active && "bg-primary/10 text-primary"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* User menu */}
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
